import { useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { InterventionMemory } from "@/lib/behavior/interventionMemory";
import type { CoachSessionPrepContinuityViewModel } from "@/lib/behavior/viewModels/buildCoachSessionPrepContinuity";
import type { ContinuityRuntime } from "@/lib/behavior/viewModels/buildContinuityRuntime";
import { cn } from "@/lib/utils";

interface ContinuityPanelProps {
  runtime?: ContinuityRuntime;
  recordSummary?: ContinuityRuntime["recordSummary"];
  synthesis?: (CoachSessionPrepContinuityViewModel & {
    interventionMemory?: InterventionMemory | null;
    reviewLabel?: string;
    onOpenReview?: () => void;
  }) | null;
  className?: string;
}

function buildSharedTopSummary(params: {
  changed: string[];
  carryForward: string[];
  next: string[];
}) {
  const normalizeClause = (value: string | null) => value?.replace(/[.!?]+$/g, "").trim() || null;
  const isReadableClause = (value: string | null) => {
    if (!value) return false;
    if (value.length < 18) return false;
    if (/^[a-z]/.test(value)) return false;
    if (value.includes("?")) return false;
    return true;
  };
  const changed = normalizeClause(params.changed[0] || null);
  const carryForward = normalizeClause(
    params.carryForward.find((line) => line !== params.changed[0]) || params.carryForward[0] || null,
  );
  const next = normalizeClause(params.next[0] || null);

  if (!isReadableClause(changed) || !isReadableClause(carryForward) || !isReadableClause(next)) {
    return "Across the last few sessions, one theme is getting clearer, one is carrying forward, and one remains worth revisiting.";
  }

  if (changed && carryForward && next) {
    return `Across the last few sessions, ${changed} keeps shifting while ${carryForward} carries forward and ${next} stays under probe.`;
  }

  if (changed && carryForward) {
    return `Across the last few sessions, ${changed} keeps shifting while ${carryForward} carries forward.`;
  }

  if (changed && next) {
    return `Across the last few sessions, ${changed} keeps shifting while ${next} stays under probe.`;
  }

  if (carryForward && next) {
    return `Across the last few sessions, ${carryForward} carries forward and ${next} stays under probe.`;
  }

  return changed || carryForward || next || "Shared continuity will sharpen as more shared evidence accumulates.";
}

function buildDrillInExplanation(title: string) {
  switch (title) {
    case "What Changed":
      return "This is the shift becoming more visible across the recent sessions.";
    case "What Carries Forward":
      return "This is the pattern that still seems active and worth holding onto across sessions.";
    case "What To Probe Next":
    case "What Next":
      return "This is the thread that still needs a closer look in the next conversation.";
    default:
      return "This gives a little more context behind the continuity signal.";
  }
}

function Section(props: { title: string; lines: string[]; emptyLabel: string }) {
  const [expanded, setExpanded] = useState(false);
  const previewLines = expanded ? props.lines : props.lines.slice(0, 2);
  const hasOverflow = props.lines.length > 2;

  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{props.title}</p>
      {previewLines.length > 0 ? (
        previewLines.map((line, idx) => (
          <p key={`${props.title}-${line}-${idx}`} className="text-sm text-foreground">
            - {line}
          </p>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">{props.emptyLabel}</p>
      )}
      {hasOverflow ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto px-0 py-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      ) : null}
    </div>
  );
}

function SynthesisCard(props: {
  title: string;
  lines: string[];
  examples?: string[];
  emptyLabel: string;
  badgeLabel: string;
  badgeClassName: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [leadLine, ...remainingLines] = props.lines;
  const supportLine = remainingLines[0] ?? null;
  const fallbackExamples = [supportLine, ...remainingLines.slice(1)].filter(Boolean).slice(0, 2);
  const evidenceExamples = (props.examples ?? fallbackExamples).filter(Boolean).slice(0, 2);
  const hasDrillIn = props.lines.length > 0;
  const explanation = buildDrillInExplanation(props.title);

  return (
    <div className="rounded-[1.5rem] border border-border/60 bg-muted/[0.14] p-4 sm:p-5">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{props.title}</p>
        <Badge variant="outline" className={props.badgeClassName}>
          {props.badgeLabel}
        </Badge>
      </div>
      <div className="mt-3.5 space-y-2.5">
        <p className="text-base font-medium leading-7 text-foreground sm:text-[1.05rem]">
          {leadLine || props.emptyLabel}
        </p>
        {supportLine ? <p className="text-sm leading-6 text-muted-foreground">{supportLine}</p> : null}
        {expanded ? (
          <div className="space-y-3 rounded-2xl border border-border/50 bg-background/40 p-3.5">
            <p className="text-sm leading-6 text-muted-foreground">{explanation}</p>
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Examples</p>
              {evidenceExamples.length > 0 ? (
                evidenceExamples.map((line, idx) => (
                  <p key={`${props.title}-example-${line}-${idx}`} className="text-sm leading-6 text-muted-foreground">
                    - {line}
                  </p>
                ))
              ) : (
                <p className="text-sm leading-6 text-muted-foreground">No clear evidence is surfaced here yet.</p>
              )}
            </div>
          </div>
        ) : null}
        {hasDrillIn ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-0 py-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Show less" : "Show more"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function RecordAnchor({ summary }: { summary: ContinuityRuntime["recordSummary"] }) {
  if (!summary) return null;
  return (
    <div className="rounded-lg border border-border/60 bg-background/70 p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{summary.label}</p>
      <p className="mt-1 text-sm font-medium text-foreground">{summary.counterText}</p>
      <p className="mt-1 text-xs text-muted-foreground">{summary.accumulationText}</p>
    </div>
  );
}

export function ContinuityPanel({ runtime, recordSummary, synthesis, className }: ContinuityPanelProps) {
  if (synthesis) {
    const latestCoachGrowthRecord = synthesis.latestCoachGrowthRecord;
    const useCompactHeroSummary = synthesis.topSummary.length > 220;
    const latestGrowthLabel = latestCoachGrowthRecord
      ? `${latestCoachGrowthRecord.source} • ${format(new Date(latestCoachGrowthRecord.created_at), "MMM d, yyyy · h:mm a")}`
      : null;

    return (
      <Card className={className}>
        <CardContent className="space-y-5 p-4 sm:p-6">
          <div className="rounded-[1.75rem] border border-border/60 bg-[radial-gradient(circle_at_top_left,rgba(169,128,255,0.16),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))] p-5 sm:p-6">
            <div className="grid gap-5 md:grid-cols-[5.5rem_minmax(0,1fr)]">
              <div className="hidden md:block">
                <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Session Continuity</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Carry forward</p>
                <div className="mt-4 flex min-h-[9.5rem] justify-center">
                  <div className="relative w-6">
                    <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gradient-to-b from-violet-400/90 via-amber-300/70 to-rose-400/80" />
                    <span className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 rounded-full border border-violet-300/30 bg-violet-500 shadow-[0_0_20px_rgba(168,85,247,0.55)]" />
                    <span className="absolute left-1/2 top-[4.4rem] h-4 w-4 -translate-x-1/2 rounded-full border border-amber-200/30 bg-amber-400 shadow-[0_0_18px_rgba(251,191,36,0.45)]" />
                    <span className="absolute left-1/2 bottom-0 h-4 w-4 -translate-x-1/2 rounded-full border border-rose-200/30 bg-rose-400 shadow-[0_0_18px_rgba(251,113,133,0.45)]" />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2.5">
                  <h3
                    data-testid="continuity-synthesis-title"
                    className={cn(
                      "max-w-4xl text-balance",
                      useCompactHeroSummary
                        ? "text-[1.08rem] font-medium leading-7 tracking-normal"
                        : "text-[clamp(1.55rem,2.8vw,2.35rem)] font-semibold leading-[1.1] tracking-tight",
                    )}
                  >
                    {synthesis.topSummary}
                  </h3>
                  <p className="max-w-2xl text-[0.92rem] leading-6 text-muted-foreground">
                    Synthesized from the last few saved sessions so a coach can orient fast without reading a dense recap.
                  </p>
                  <RecordAnchor summary={recordSummary} />
                </div>

                <div className="space-y-4">
                  <SynthesisCard
                    title="What Changed"
                    lines={synthesis.whatChanged}
                    emptyLabel="No new change signal saved yet."
                    badgeLabel="Emerging more clearly"
                    badgeClassName="border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  />
                  <SynthesisCard
                    title="What Carries Forward"
                    lines={synthesis.whatCarriesForward}
                    emptyLabel="Nothing is named to carry forward yet."
                    badgeLabel="Still in tension"
                    badgeClassName="border-amber-500/30 bg-amber-500/10 text-amber-300"
                  />
                  <SynthesisCard
                    title="What To Probe Next"
                    lines={synthesis.whatToProbeNext}
                    emptyLabel="No explicit next step is saved yet."
                    badgeLabel="Coach move"
                    badgeClassName="border-rose-500/30 bg-rose-500/10 text-rose-300"
                  />
                </div>
              </div>
            </div>
          </div>
          {latestCoachGrowthRecord ? (
            <div className="space-y-3 rounded-lg border border-border/60 bg-background/60 p-4">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Latest saved coaching note</p>
                {latestGrowthLabel ? (
                  <p className="text-sm text-muted-foreground">{latestGrowthLabel}</p>
                ) : null}
              </div>
              {synthesis.interventionMemory ? (
                <div className="space-y-3 rounded-md border border-border/60 bg-background p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Intervention memory</p>
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Raised previously</p>
                    <p className="text-sm text-foreground">{synthesis.interventionMemory.raisedPreviously.focus || "No explicit prior focus saved."}</p>
                    {synthesis.interventionMemory.raisedPreviously.nextSignal ? (
                      <p className="text-sm text-muted-foreground">
                        Previously watching for: {synthesis.interventionMemory.raisedPreviously.nextSignal}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Changed since raised</p>
                    <p className="text-sm text-foreground">
                      {synthesis.interventionMemory.changedSinceRaised
                        ? "Yes, the latest saved coaching evidence is pointing at a different focus now."
                        : "Not yet, the latest saved coaching evidence is still reinforcing the same focus."}
                    </p>
                  </div>
                </div>
              ) : null}
              {synthesis.onOpenReview && synthesis.latestCoachGrowthRecord?.transcriptUploadId ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={synthesis.onOpenReview}
                >
                  {synthesis.reviewLabel || "Open review"}
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (!runtime) return null;
  if (runtime.headline === "Shared Continuity") {
    const isEmptySharedContinuity = runtime.empty || runtime.summary === "No continuity summary yet.";
    const topSummary = buildSharedTopSummary({
      changed: runtime.changed,
      carryForward: runtime.carryForward,
      next: runtime.next,
    });
    const sharedHeroSummary = isEmptySharedContinuity
      ? "Your throughline will start with the moments you choose to save."
      : topSummary;
    const useCompactHeroSummary = sharedHeroSummary.length > 220;

    return (
      <Card className={className}>
        <CardContent className="space-y-5 p-4 sm:p-6">
          <div className="rounded-[1.75rem] border border-border/60 bg-[radial-gradient(circle_at_top_left,rgba(169,128,255,0.16),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))] p-5 sm:p-6">
            <div className="grid gap-5 md:grid-cols-[5.5rem_minmax(0,1fr)]">
              <div className="hidden md:block">
                <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {isEmptySharedContinuity ? "Throughline" : "Thread history and detail"}
                </p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  {isEmptySharedContinuity ? "Waiting for moments" : "Carry forward from the active thread"}
                </p>
                <div className="mt-4 flex min-h-[9.5rem] justify-center">
                  <div className="relative w-6">
                    <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-gradient-to-b from-violet-400/90 via-amber-300/70 to-rose-400/80" />
                    <span className="absolute left-1/2 top-0 h-4 w-4 -translate-x-1/2 rounded-full border border-violet-300/30 bg-violet-500 shadow-[0_0_20px_rgba(168,85,247,0.55)]" />
                    <span className="absolute left-1/2 top-[4.4rem] h-4 w-4 -translate-x-1/2 rounded-full border border-amber-200/30 bg-amber-400 shadow-[0_0_18px_rgba(251,191,36,0.45)]" />
                    <span className="absolute left-1/2 bottom-0 h-4 w-4 -translate-x-1/2 rounded-full border border-rose-200/30 bg-rose-400 shadow-[0_0_18px_rgba(251,113,133,0.45)]" />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2.5">
                  <h3
                    data-testid="continuity-runtime-shared-title"
                    className={cn(
                      "max-w-4xl text-balance",
                      useCompactHeroSummary
                        ? "text-[1.08rem] font-medium leading-7 tracking-normal"
                        : "text-[clamp(1.55rem,2.8vw,2.35rem)] font-semibold leading-[1.1] tracking-tight",
                    )}
                  >
                    {sharedHeroSummary}
                  </h3>
                  <p className="max-w-2xl text-[0.92rem] leading-6 text-muted-foreground">
                    {isEmptySharedContinuity
                      ? "Save a leadership moment, complete a reflection, or upload meeting evidence to begin building continuity over time."
                      : "This panel keeps the same active thread in view so you can inspect how it has evolved over time."}
                  </p>
                  <p className="max-w-2xl text-[0.92rem] leading-6 text-muted-foreground">
                    {runtime.summary}
                  </p>
                  <RecordAnchor summary={runtime.recordSummary} />
                </div>

                {isEmptySharedContinuity ? (
                  <div className="rounded-[1.5rem] border border-border/60 bg-muted/[0.14] p-4 sm:p-5">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">What will appear here</p>
                    <p className="mt-3 text-base font-medium leading-7 text-foreground sm:text-[1.05rem]">
                      Once you have saved a few moments, this panel will show what is changing, what is carrying forward, and what may be worth revisiting.
                    </p>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      Nothing has been inferred yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                  <SynthesisCard
                    title="What Changed"
                    lines={runtime.changed}
                    examples={runtime.examples.changed}
                    emptyLabel="No new change signal saved yet."
                    badgeLabel="Emerging more clearly"
                    badgeClassName="border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  />
                  <SynthesisCard
                    title="What Carries Forward"
                    lines={runtime.carryForward}
                    examples={runtime.examples.carryForward}
                    emptyLabel="Nothing is named to carry forward yet."
                    badgeLabel="Still in tension"
                    badgeClassName="border-amber-500/30 bg-amber-500/10 text-amber-300"
                  />
                  <SynthesisCard
                    title="What Next"
                    lines={runtime.next}
                    examples={runtime.examples.next}
                    emptyLabel="No explicit next step is saved yet."
                    badgeLabel="Next to revisit"
                    badgeClassName="border-rose-500/30 bg-rose-500/10 text-rose-300"
                  />
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-h3">{runtime.headline}</CardTitle>
        <p className="text-sm text-muted-foreground">{runtime.summary}</p>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0 sm:p-6 sm:pt-0">
        <RecordAnchor summary={runtime.recordSummary} />
        <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
          <p className="text-sm text-foreground">{runtime.narrative}</p>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          <Section
            title="What Changed"
            lines={runtime.changed}
            emptyLabel="No new change signal saved yet."
          />
          <Section
            title="Carry Forward"
            lines={runtime.carryForward}
            emptyLabel="Nothing is named to carry forward yet."
          />
          <Section
            title="What Next"
            lines={runtime.next}
            emptyLabel="No explicit next step is saved yet."
          />
        </div>
      </CardContent>
    </Card>
  );
}
